/**
 * Builds the self-contained HTML for a printable payment receipt.
 *
 * The markup is written into a pop-up window by `openPrintWindow`, so every
 * style has to travel with the document — the dashboard's Tailwind theme is not
 * available there. The layout deliberately mirrors the invoice print
 * (`buildInvoicePrintHtml`): same teal brand colour, hospital letterhead,
 * signature/seal block and footer, so every financial document a patient
 * receives looks like it came from the same hospital.
 *
 * "Logical" structure, in the order a reader needs it:
 *   1. Who the money came from and which receipt this is,
 *   2. how much and in words,
 *   3. what it was applied to (invoice, reference, notes) and the balance left,
 *   4. who handled it and where to ask questions (footer).
 */

const CURRENCY = '৳';

const METHOD_LABELS: Record<string, string> = {
  cash: 'Cash',
  card: 'Card',
  upi: 'UPI',
  bank_transfer: 'Bank Transfer',
  insurance: 'Insurance',
};

export interface ReceiptHospital {
  name: string;
  address?: string;
  email?: string;
  website?: string;
  hotline?: string;
  logo?: string;
  certified?: string;
  signature?: string;
  seal?: string;
  signatoryName?: string;
}

export interface ReceiptPayment {
  paymentNumber?: string;
  amount?: number;
  method?: string;
  transactionId?: string;
  notes?: string;
  paymentDate?: string;
  createdAt?: string;
  patientId?: string;
  patient?: {
    firstName?: string;
    lastName?: string;
    mobileNumber?: string;
    patientCode?: string;
  };
  invoice?: {
    invoiceNumber?: string;
    status?: string;
    total?: number;
    paidAmount?: number;
    dueAmount?: number;
  };
  receivedBy?: { name?: string; firstName?: string; lastName?: string };
}

function escapeHtml(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function fmtMoney(value: number | undefined | null): string {
  const n = Number(value) || 0;
  const sign = n < 0 ? '−' : '';
  return `${sign}${CURRENCY}${Math.abs(n).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

function patientName(payment: ReceiptPayment): string {
  const patient = payment.patient;
  const name = patient ? `${patient.firstName || ''} ${patient.lastName || ''}`.trim() : '';
  return name || payment.patientId?.slice(0, 8) || '—';
}

function receivedByName(payment: ReceiptPayment): string {
  const user = payment.receivedBy;
  if (!user) return '—';
  return user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || '—';
}

function fmtDate(value?: string | null): { date: string; time: string } {
  if (!value) return { date: '—', time: '' };
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return { date: '—', time: '' };
  return {
    date: d.toLocaleDateString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit' }),
    time: d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
  };
}

/* ------------------------------------------------------------------ */
/* Amount in words (Bangladeshi numbering: thousand / lakh / crore)     */
/* ------------------------------------------------------------------ */

const ONES = [
  '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
  'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
  'Seventeen', 'Eighteen', 'Nineteen',
];
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

/** Words for 0–99. */
function twoDigits(n: number): string {
  if (n < 20) return ONES[n];
  const tens = Math.floor(n / 10);
  const ones = n % 10;
  return `${TENS[tens]}${ones ? `-${ONES[ones]}` : ''}`;
}

/** Words for 0–99,99,99,999 using the Indian/BD grouping. */
function numberToWords(value: number): string {
  const n = Math.floor(Math.abs(value));
  if (n === 0) return 'Zero';

  const parts: string[] = [];
  const crore = Math.floor(n / 10000000);
  let rest = n % 10000000;
  const lakh = Math.floor(rest / 100000);
  rest %= 100000;
  const thousand = Math.floor(rest / 1000);
  rest %= 1000;
  const hundred = Math.floor(rest / 100);
  const tail = rest % 100;

  if (crore) parts.push(`${twoDigits(crore)} Crore`);
  if (lakh) parts.push(`${twoDigits(lakh)} Lakh`);
  if (thousand) parts.push(`${twoDigits(thousand)} Thousand`);
  if (hundred) parts.push(`${ONES[hundred]} Hundred`);
  if (tail) parts.push(`${parts.length ? 'and ' : ''}${twoDigits(tail)}`);

  return parts.join(' ').trim();
}

/** Full "Taka … and … Poisha Only" sentence for a money value. */
export function amountInWords(value: number | undefined | null): string {
  const amount = Number(value) || 0;
  if (amount === 0) return 'Zero Taka Only';

  const absolute = Math.abs(amount);
  const taka = Math.floor(absolute);
  const poisha = Math.round((absolute - taka) * 100);

  let words = `${numberToWords(taka)} Taka`;
  if (poisha > 0) words += ` and ${twoDigits(poisha)} Poisha`;
  words += ' Only';
  return amount < 0 ? `Minus ${words}` : words;
}

/* ------------------------------------------------------------------ */
/* Document                                                            */
/* ------------------------------------------------------------------ */

export function buildPaymentReceiptHtml({
  payment,
  hospital,
  printedBy,
}: {
  payment: ReceiptPayment;
  hospital: ReceiptHospital;
  printedBy?: string;
}): string {
  const amount = Number(payment.amount || 0);
  const isRefund = amount < 0;

  const receivedBy = receivedByName(payment);
  const patient = payment.patient || {};
  const invoice = payment.invoice;
  const { date: dateStr, time: timeStr } = fmtDate(payment.paymentDate || payment.createdAt);

  const methodLabel = METHOD_LABELS[payment.method || ''] || payment.method || '—';
  const docLabel = isRefund ? 'Refund Receipt' : 'Payment Receipt';
  const amountLabel = isRefund ? 'Amount Refunded' : 'Amount Received';
  const statusText = isRefund ? 'Refunded' : 'Paid';
  const statusStyle = isRefund
    ? 'background:#fee2e2;color:#b91c1c;'
    : 'background:#dcfce7;color:#15803d;';
  const accent = isRefund ? '#b91c1c' : '#0f766e';

  const contact = [
    hospital.hotline ? `Hotline: ${hospital.hotline}` : '',
    hospital.email,
    hospital.website,
  ]
    .filter(Boolean)
    .join(' · ');

  const hasInvoiceTotals =
    !!invoice && (invoice.total != null || invoice.paidAmount != null || invoice.dueAmount != null);

  return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>${docLabel} ${escapeHtml(payment.paymentNumber)}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: 'Segoe UI', Arial, Helvetica, sans-serif; color: #0f172a; margin: 0; padding: 32px; background: #ffffff; font-size: 12px; }
  .doc { max-width: 800px; margin: 0 auto; }

  .accent { height: 6px; border-radius: 999px; background: linear-gradient(90deg, ${accent} 0%, ${accent}cc 45%, #e2e8f0 100%); }

  .header { display: flex; justify-content: space-between; align-items: flex-start; gap: 24px; padding: 16px 0 18px; border-bottom: 3px solid ${accent}; }
  .brand { display: flex; align-items: flex-start; gap: 12px; min-width: 0; }
  .brand .logo { height: 52px; width: 52px; border-radius: 12px; object-fit: contain; }
  .brand .mark { height: 52px; width: 52px; border-radius: 12px; background: ${accent}1a; color: ${accent}; display: flex; align-items: center; justify-content: center; font-size: 22px; font-weight: 800; }
  .brand h1 { margin: 0; font-size: 20px; font-weight: 800; color: #0f172a; }
  .brand .sub { font-size: 11px; color: #64748b; margin-top: 3px; line-height: 1.5; }
  .title { text-align: right; white-space: nowrap; }
  .title .label { font-size: 20px; font-weight: 800; letter-spacing: 1.5px; text-transform: uppercase; color: ${accent}; }
  .title .no { font-size: 11px; color: #64748b; margin-top: 4px; }
  .badge { display: inline-block; margin-top: 6px; padding: 3px 11px; border-radius: 999px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .5px; }

  .grid { display: flex; border: 1px solid #e2e8f0; border-radius: 10px; overflow: hidden; margin-top: 20px; }
  .grid .cell { flex: 1; padding: 12px 16px; border-right: 1px solid #e2e8f0; }
  .grid .cell.wide { flex: 1.7; }
  .grid .cell:last-child { border-right: 0; }
  .grid h3 { margin: 0 0 6px; font-size: 9px; font-weight: 800; letter-spacing: 1.2px; text-transform: uppercase; color: #64748b; }
  .grid p { margin: 0; line-height: 1.6; }
  .strong { font-weight: 700; }

  .amount-band { display: flex; justify-content: space-between; align-items: center; gap: 24px; margin-top: 18px; padding: 16px 20px; border-radius: 12px; background: ${accent}0f; border: 1px solid ${accent}33; }
  .amount-band .label { font-size: 10px; font-weight: 800; letter-spacing: 1.4px; text-transform: uppercase; color: ${accent}; }
  .amount-band .words { font-size: 11px; color: #475569; margin-top: 6px; line-height: 1.5; max-width: 420px; }
  .amount-band .value { font-size: 30px; font-weight: 800; color: ${accent}; white-space: nowrap; letter-spacing: -.5px; }

  .bottom { display: flex; justify-content: space-between; align-items: flex-start; gap: 32px; margin-top: 22px; }
  .details { flex: 1; min-width: 0; }
  .details .row { display: flex; justify-content: space-between; gap: 16px; padding: 7px 0; border-bottom: 1px dashed #e2e8f0; font-size: 12px; }
  .details .row:last-child { border-bottom: 0; }
  .details .row .k { color: #64748b; }
  .details .row .v { font-weight: 600; text-align: right; }
  .details h4, .notes h4 { margin: 0 0 6px; font-size: 9px; font-weight: 800; letter-spacing: 1px; text-transform: uppercase; color: #64748b; }
  .notes { margin-top: 14px; }
  .notes p { margin: 0; font-size: 11px; color: #475569; line-height: 1.6; white-space: pre-wrap; }

  .totals { width: 300px; border: 1px solid #e2e8f0; border-radius: 10px; overflow: hidden; }
  .totals .head { background: #0f172a; color: #ffffff; padding: 8px 16px; font-size: 9px; font-weight: 800; letter-spacing: 1.2px; text-transform: uppercase; }
  .totals .body { padding: 10px 16px; }
  .totals .foot { border-top: 1px solid #e2e8f0; background: #f8fafc; padding: 10px 16px; }
  .totals .line { display: flex; justify-content: space-between; padding: 4px 0; font-size: 12px; }
  .totals .foot .line { font-weight: 700; }
  .totals .foot .line.grand { font-size: 15px; }
  .success { color: #15803d; }
  .danger { color: #b91c1c; }
  .muted { color: #94a3b8; }

  .signature-block { display: flex; justify-content: flex-end; margin-top: 54px; }
  .signature-box { position: relative; width: 240px; text-align: center; }
  .signature-box .signature-img { height: 54px; max-width: 200px; object-fit: contain; display: block; margin: 0 auto 2px; }
  .signature-box .seal-img { position: absolute; top: -16px; right: -12px; height: 72px; width: 72px; object-fit: contain; opacity: .85; }
  .signature-box .line { border-top: 1px solid #0f172a; margin-top: 6px; }
  .signature-box .signature-label { margin-top: 4px; font-size: 11px; color: #374151; }
  .signature-box .signatory-name { margin-top: 2px; font-size: 10px; color: #64748b; }

  .footer { margin-top: 40px; border-top: 1px solid #e2e8f0; padding-top: 12px; font-size: 10px; color: #64748b; text-align: center; line-height: 1.6; }
  .footer .note { color: #94a3b8; margin-top: 4px; }

  /* Zero the @page margin so the browser does not print its default
     header/footer; the visual margin is provided by body padding. */
  @media print { @page { margin: 0; } body { padding: 14mm 12mm; } }
</style>
</head>
<body>
  <div class="doc">
    <div class="accent"></div>

    <div class="header">
      <div class="brand">
        ${hospital.logo
          ? `<img class="logo" src="${escapeHtml(hospital.logo)}" alt="logo" />`
          : `<div class="mark">${escapeHtml((hospital.name || 'H').trim().charAt(0).toUpperCase())}</div>`}
        <div>
          <h1>${escapeHtml(hospital.name)}</h1>
          ${hospital.address ? `<div class="sub">${escapeHtml(hospital.address)}</div>` : ''}
          ${contact ? `<div class="sub">${escapeHtml(contact)}</div>` : ''}
        </div>
      </div>
      <div class="title">
        <div class="label">${escapeHtml(docLabel)}</div>
        <div class="no">No. ${escapeHtml(payment.paymentNumber || '—')}</div>
        <span class="badge" style="${statusStyle}">${escapeHtml(statusText)}</span>
      </div>
    </div>

    <div class="grid">
      <div class="cell wide">
        <h3>Received From</h3>
        <p class="strong">${escapeHtml(patientName(payment))}</p>
        ${patient.mobileNumber ? `<p>${escapeHtml(patient.mobileNumber)}</p>` : ''}
        ${patient.patientCode ? `<p>Code: ${escapeHtml(patient.patientCode)}</p>` : ''}
      </div>
      <div class="cell">
        <h3>Receipt No.</h3>
        <p class="strong">${escapeHtml(payment.paymentNumber || '—')}</p>
      </div>
      <div class="cell">
        <h3>Payment Date</h3>
        <p class="strong">${escapeHtml(dateStr)}</p>
        ${timeStr ? `<p>${escapeHtml(timeStr)}</p>` : ''}
      </div>
      <div class="cell">
        <h3>Method</h3>
        <p class="strong">${escapeHtml(methodLabel)}</p>
      </div>
    </div>

    <div class="amount-band">
      <div>
        <div class="label">${escapeHtml(amountLabel)}</div>
        <div class="words">${escapeHtml(amountInWords(amount))}</div>
      </div>
      <div class="value">${escapeHtml(fmtMoney(amount))}</div>
    </div>

    <div class="bottom">
      <div class="details">
        <div class="row"><span class="k">Against Invoice</span><span class="v">${escapeHtml(invoice?.invoiceNumber || '—')}</span></div>
        <div class="row"><span class="k">Reference / Txn ID</span><span class="v">${escapeHtml(payment.transactionId || '—')}</span></div>
        <div class="row"><span class="k">Received By</span><span class="v">${escapeHtml(receivedBy)}</span></div>
        ${payment.notes
          ? `<div class="notes"><h4>Notes</h4><p>${escapeHtml(payment.notes)}</p></div>`
          : ''}
      </div>
      ${hasInvoiceTotals
        ? `<div class="totals">
        <div class="head">Invoice Position</div>
        <div class="body">
          <div class="line"><span>Invoice Total</span><span>${escapeHtml(fmtMoney(invoice?.total))}</span></div>
          <div class="line"><span>Total Paid</span><span class="success">${escapeHtml(fmtMoney(invoice?.paidAmount))}</span></div>
        </div>
        <div class="foot">
          <div class="line grand"><span>Balance Due</span><span class="danger">${escapeHtml(fmtMoney(invoice?.dueAmount))}</span></div>
        </div>
      </div>`
        : ''}
    </div>

    <div class="signature-block">
      <div class="signature-box">
        ${hospital.seal ? `<img class="seal-img" src="${escapeHtml(hospital.seal)}" alt="Hospital seal" />` : ''}
        ${hospital.signature ? `<img class="signature-img" src="${escapeHtml(hospital.signature)}" alt="Authorized signature" />` : ''}
        <div class="line"></div>
        <div class="signature-label">Received By / Authorized Signature</div>
        ${hospital.signatoryName ? `<div class="signatory-name">${escapeHtml(hospital.signatoryName)}</div>` : ''}
      </div>
    </div>

    <div class="footer">
      ${hospital.name ? `<div>${escapeHtml(hospital.name)}</div>` : ''}
      ${hospital.certified ? `<div>${escapeHtml(hospital.certified)}</div>` : ''}
      <div class="note">
        This is a computer-generated receipt and is valid without a handwritten signature.
        ${printedBy ? `Printed by ${escapeHtml(printedBy)} on ${escapeHtml(new Date().toLocaleString())}.` : `Printed on ${escapeHtml(new Date().toLocaleString())}.`}
      </div>
    </div>
  </div>
</body>
</html>`;
}
